import { type PlanPricing, type PricingModel, type Subscription, type SubscriptionPlan } from '@air/api/types';
import { BYTES_GIGABYTE, formatBytes } from '@air/utilities';

import { type CreditUsageStats } from '~/components/CreditUsageMeter/types';

const CREDITS_PER_STORAGE_GB = 6;
const CREDITS_PER_AI_GENERATION = 5;
const GB_PER_TB = 1000;

const FULL_CREDIT_COUNT_FORMATTER = new Intl.NumberFormat('en-US');
const COMPACT_CREDIT_COUNT_FORMATTER = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  compactDisplay: 'short',
});

export const formatCreditCount = (credits: number, options?: { compact?: boolean }) =>
  (options?.compact ? COMPACT_CREDIT_COUNT_FORMATTER : FULL_CREDIT_COUNT_FORMATTER).format(credits);

export const formatMonthlyCredits = (credits: number) => `${formatCreditCount(credits)} monthly credits`;

type GiftCreditsBalanceSource = {
  pricingModel: PricingModel | undefined;
  /** Balance from credit stats. getStats omits this field when the balance is 0. */
  statsGiftCreditsBalance: number | undefined;
};

// Stats copy subscription.giftCreditsBalance and omit it at 0, so a second read of the
// subscription can show a gift that was already spent. Per-member plans never count a gift.
export const getGiftCreditsBalance = ({ pricingModel, statsGiftCreditsBalance }: GiftCreditsBalanceSource) =>
  pricingModel === 'credit' || pricingModel === 'fixed' ? (statsGiftCreditsBalance ?? 0) : 0;

export type CreditUtilization = {
  storage: string;
  aiGenerations: string;
};

/**
 * Translates a credit count into the resources it can buy: storage capacity (formatted, e.g.
 * "10 GB") and AI-generation count (formatted, e.g. "166"). Used wherever we surface "what can
 * N credits do?" — callers compose the final sentence themselves.
 */
export const getPotentialCreditUtilization = (credits: number): CreditUtilization => {
  const storageGB = Math.floor(credits / CREDITS_PER_STORAGE_GB);
  // Past 1 TB, surface one decimal for non-round TB values (e.g. "1.5 TB") so we don't round
  // away meaningful precision the user is paying for.
  const numberOfDecimals = storageGB > GB_PER_TB && storageGB % GB_PER_TB !== 0 ? 1 : 0;

  return {
    storage: formatBytes(storageGB * BYTES_GIGABYTE, { numberOfDecimals }),
    aiGenerations: formatCreditCount(Math.floor(credits / CREDITS_PER_AI_GENERATION)),
  };
};

export const formatPotentialCreditUtilization = (credits: number) => {
  const { storage, aiGenerations } = getPotentialCreditUtilization(credits);

  return `Up to ${storage} of storage or ${aiGenerations} AI-generated images`;
};

export type CreditPackEligibleSubscription = Subscription & {
  maxCreditPacks: number;
  creditPackSize: number;
};

const CREDIT_ENFORCEABLE_FIXED_PLANS = new Set<SubscriptionPlan>([
  'basic',
  'starter',
  'business',
  'creator',
  'plus',
  'pro',
]);
const CREDIT_PACK_PLANS = new Set<SubscriptionPlan>(['starter', 'business', 'enterprise']);

/**
 * Plans the backend can enforce credits on. Only decides whether to fetch credit stats: whether
 * credits are enforced right now is `isEnforced` on the stats, which the backend owns.
 */
export const canHaveEnforcedCredits = (subscription: Pick<Subscription, 'plan' | 'pricingModel'> | undefined) =>
  subscription?.pricingModel === 'credit' ||
  (subscription?.pricingModel === 'fixed' && CREDIT_ENFORCEABLE_FIXED_PLANS.has(subscription.plan));

/**
 * Whether credit UI (meters, banners, credit pack offers) applies. Fixed plans only show it once the
 * backend enforces their credits; `creditUsageEnabledForLegacyPlans` opts every plan in.
 */
export const isCreditUsageVisible = ({
  pricingModel,
  isEnforced,
  creditUsageEnabledForLegacyPlans,
}: {
  pricingModel: PricingModel | undefined;
  isEnforced: boolean;
  creditUsageEnabledForLegacyPlans: boolean;
}) => creditUsageEnabledForLegacyPlans || pricingModel === 'credit' || isEnforced;

/**
 * Credit stats from the query cache. `isEnforced` and `allowCreditOverage` are optional
 * because another tab may write a response that omits them.
 */
export type CreditStatsResponse = Omit<CreditUsageStats, 'isEnforced' | 'allowCreditOverage'> &
  Partial<Pick<CreditUsageStats, 'isEnforced' | 'allowCreditOverage'>>;

/** Fills `isEnforced` and `allowCreditOverage` when a response omits them. */
export const normalizeCreditStats = (stats: CreditStatsResponse): CreditUsageStats => ({
  ...stats,
  isEnforced: stats.isEnforced ?? false,
  allowCreditOverage: stats.allowCreditOverage ?? false,
});

/** Plans the backend sells credit packs on. Enough to show packs the workspace already has. */
export const canHaveCreditPacks = (
  subscription: Subscription | undefined,
): subscription is CreditPackEligibleSubscription =>
  !!subscription &&
  CREDIT_PACK_PLANS.has(subscription.plan) &&
  subscription.pricingModel !== 'perMember' &&
  !!subscription.maxCreditPacks &&
  !!subscription.creditPackSize;

/** Whether to offer buying credit packs. Fixed plans also need the backend to enforce their credits. */
export const isEligibleForCreditPacks = (
  subscription: Subscription | undefined,
  creditStats: Pick<CreditUsageStats, 'isEnforced'> | undefined,
): subscription is CreditPackEligibleSubscription =>
  canHaveCreditPacks(subscription) && (subscription.pricingModel === 'credit' || creditStats?.isEnforced === true);

/** Packs already on the subscription, or a pending pack change, that can be reduced or removed. */
export const canManageExistingCreditPacks = (
  subscription: Subscription | undefined,
): subscription is CreditPackEligibleSubscription =>
  canHaveCreditPacks(subscription) && (subscription.creditPackQuantity > 0 || !!subscription.pendingPackChange);

/**
 * Open the credit-pack modal to buy packs, or to change packs the workspace already has.
 * A failed or unenforced stats response blocks new purchases and still allows reductions.
 */
export const canOpenCreditPackModal = (
  subscription: Subscription | undefined,
  creditStats: Pick<CreditUsageStats, 'isEnforced'> | undefined,
) => isEligibleForCreditPacks(subscription, creditStats) || canManageExistingCreditPacks(subscription);

export type SubscriptionCreditPackInfo = {
  /** Number of packs currently on the subscription. */
  quantity: number;
  /** Credits per pack. */
  size: number;
  /** Total monthly credits granted by the packs (`quantity * size`). */
  credits: number;
  /** Maximum packs purchasable on this subscription. */
  maxQuantity: number;
  /** Per-pack price for the subscription's current billing cycle, in cents. */
  pricePerPackCents: number;
  /** Aggregate pack price for the cycle (`quantity * pricePerPackCents`), in cents. */
  totalPriceCents: number;
};

export const getSubscriptionCreditPackInfo = ({
  subscription,
  plan,
}: {
  subscription: Subscription | undefined;
  plan: PlanPricing | undefined;
}): SubscriptionCreditPackInfo | null => {
  if (!plan || !subscription || !canHaveCreditPacks(subscription) || !subscription.billingCycle) {
    return null;
  }

  let pricePerPackCents: number | undefined;

  if (subscription.billingCycle === 'monthly') {
    pricePerPackCents = plan.creditPackPriceMonthly;
  } else if (subscription.billingCycle === 'annual') {
    pricePerPackCents = plan.creditPackPriceAnnual;
  }

  if (!pricePerPackCents) {
    return null;
  }

  const quantity = subscription.creditPackQuantity;

  return {
    quantity,
    size: subscription.creditPackSize,
    credits: quantity * subscription.creditPackSize,
    maxQuantity: subscription.maxCreditPacks,
    pricePerPackCents,
    totalPriceCents: quantity * pricePerPackCents,
  };
};
